import type { PackagingShockRuleSet } from "./types.js";

// packaging-shock ruleset for FY2025-26 (1 July 2025 – 30 June 2026).
//
// This module is a presentation layer over the shared `income-tests` engine: it
// holds ONLY the figures specific to the flow-on trace (the gross-up rate, the
// RFBA reporting trigger, the MLS thresholds it crosses, and the Div293 line)
// and the FY pointers to the shared income-tests / income-tax rulesets. The
// 0.53 exempt-vs-non-exempt asymmetry is NOT duplicated here — it lives in
// income-tests and we delegate to it (see calculate.ts).
//
// Figures + the build-critical corrections come from the verification ledgers
// (packaging-income-test-shock-figures.md, adjusted-income-addbacks-figures.md),
// all CITED 2026-06-22:
//   - Gross-up: RFBA reported at the lower (type-2) rate 1.8868 regardless of
//     benefit type (figure 2 / 5). ATO worked example: $2,000.01 taxable value
//     → $3,773 RFBA.
//   - RFBA reporting trigger: taxable value > $2,000 in the FBT year (figure 5).
//   - MLS thresholds 2025-26: single $101,000 / family $202,000, +$1,500 per
//     MLS dependent child after the first (figure 4 / 6). These match the AU-08
//     ruleset (cross-consistent by design).
//   - Division 293 threshold: $250,000 (ATO).
//
// Never edit a shipped ruleset to "fix" a later year — author a new fyXXXX-XX.ts
// (and bump the gross-up rate / MLS table) when the FY rolls over.
export const fy2025_26: PackagingShockRuleSet = {
  product: "SHARED",
  fy: "2025-26",
  effectiveFrom: "2025-07-01",
  effectiveTo: "2026-06-30",
  incomeTestsFy: "2025-26",
  incomeTaxFy: "2025-26",
  sources: {
    grossUp:
      "https://atotaxrates.info/businesses/fringe-benefits-tax/reportable-fringe-benefits/",
    rfbaReporting:
      "https://www.ato.gov.au/businesses-and-organisations/hiring-and-paying-your-workers/fringe-benefits-tax/fbt-registration-lodgment-payment-and-reporting/reportable-fringe-benefits",
    rfbaConsequences:
      "https://www.ato.gov.au/individuals-and-families/jobs-and-employment-types/working-as-an-employee/reportable-fringe-benefits-for-employees/consequences-of-having-a-reportable-fringe-benefits-amount",
    mls: "https://www.ato.gov.au/individuals-and-families/medicare-and-private-health-insurance/medicare-levy-surcharge/medicare-levy-surcharge-income-thresholds-and-rates",
    div293:
      "https://www.ato.gov.au/individuals-and-families/super-for-individuals-and-families/super/growing-and-keeping-track-of-your-super/how-much-extra-tax-you-pay/division-293-tax-on-concessional-contributions-by-high-income-earners",
    ccsAti: "https://www.servicesaustralia.gov.au/what-adjusted-taxable-income",
  },
  sourcesVerifiedAt: "2026-06-22",

  // Type-2 (lower) gross-up rate for FBT years to 31 Mar 2027.
  grossUpRate: 1.8868,

  // Reportable where the TAXABLE value of benefits exceeds $2,000 (figure 5).
  rfbaReportingThreshold: 2_000,

  // MLS thresholds 2025-26 (figure 4 / 6) — cross-consistent with AU-08.
  mls: {
    singleBase: 101_000,
    familyBase: 202_000,
    familyChildIncrement: 1_500,
  },

  // Division 293 income threshold — extra 15% tax on concessional super above it.
  div293Threshold: 250_000,
};
